import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import AlertsPanel from '@components/AlertsPanel';
import EgressBoard from '@components/EgressBoard';

/**
 * Operator-only green/red status board (GA Track B): every proxy node and
 * every provider with its health signal at a glance. Server gates on
 * isAdminEmail. Auto-refreshes every 30s. See docs/ADMIN_TOOLS.md.
 */
type StatusData = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminStatus>>['data']>;

export default function AdminStatus() {
  const navigate = useNavigate();
  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const res = await streamFlowApi.adminStatus();
    if (res.success && res.data) setData(res.data);
    else if (res.error) toast.error(res.error.message);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  const nodeIssues = (data?.proxy_nodes ?? []).filter((n) => n.health_status === 'unhealthy').length;
  const provIssues = (data?.providers ?? []).filter((p) => p.health_status === 'unhealthy').length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">System Status</h1>
          <p className="text-sm text-gray-500">Proxy nodes + providers health board. Auto-refreshes every 30s.</p>
        </div>
        <div className="flex items-center gap-3">
          {(nodeIssues + provIssues) > 0
            ? <span className="inline-flex items-center gap-1.5 text-sm text-rose-600"><span className="h-2 w-2 rounded-full bg-rose-500" /> {nodeIssues + provIssues} unhealthy</span>
            : <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" /> all healthy</span>}
          <button onClick={() => void refresh()} className="text-sm text-blue-600 hover:underline">Refresh</button>
          <button onClick={() => navigate('/admin/channel-health')} className="text-sm text-blue-600 hover:underline">Channel health ▸</button>
          <button onClick={() => navigate('/admin/streaming')} className="text-sm text-blue-600 hover:underline">Live Streams ▸</button>
          <button onClick={() => navigate('/')} className="text-sm text-blue-600 hover:underline">← Dashboard</button>
        </div>
      </div>

      {/* Operator alerts + live egress chain — Phase 2 observability. */}
      <div className="space-y-6 mb-6">
        <AlertsPanel />
        <EgressBoard />
      </div>

      {loading && !data && <div className="text-sm text-gray-500">Loading…</div>}

      {data && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Proxy nodes */}
          <section className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <h2 className="font-semibold">Proxy nodes</h2>
              <span className="text-xs text-gray-500">{data.proxy_nodes.length}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data.proxy_nodes.map((n) => (
                  <tr key={n.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 w-6"><HealthDot status={n.health_status} /></td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{n.name}</div>
                      <div className="text-xs text-gray-400 font-mono break-all">{n.host}</div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {n.is_tailnet_only && <Tag>tailnet</Tag>}
                      {n.is_default_community && <Tag>default</Tag>}
                      {n.is_shared && <Tag>shared</Tag>}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-gray-400 whitespace-nowrap">{ago(n.last_health_check)}</td>
                  </tr>
                ))}
                {data.proxy_nodes.length === 0 && (
                  <tr><td className="px-3 py-6 text-center text-gray-500" colSpan={4}>No proxy nodes.</td></tr>
                )}
              </tbody>
            </table>
          </section>

          {/* Providers */}
          <section className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <h2 className="font-semibold">Providers</h2>
              <span className="text-xs text-gray-500">{data.providers.length}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data.providers.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2 w-6"><HealthDot status={p.health_status} /></td>
                    <td className="px-3 py-2">
                      <div className="font-medium">
                        {p.name} <span className="text-xs text-gray-400">({p.provider_type})</span>
                        {!p.is_active && <span className="ml-1 text-[10px] text-gray-400">inactive</span>}
                        {p.is_shared && <Tag>shared</Tag>}
                      </div>
                      {p.health_detail && <div className="text-xs text-gray-500">{p.health_detail}</div>}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-gray-400 whitespace-nowrap">{ago(p.last_health_check)}</td>
                  </tr>
                ))}
                {data.providers.length === 0 && (
                  <tr><td className="px-3 py-6 text-center text-gray-500" colSpan={3}>No providers.</td></tr>
                )}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  );
}

function HealthDot({ status }: { status: string }) {
  const c = status === 'healthy' ? 'bg-emerald-500' : status === 'unhealthy' ? 'bg-rose-500' : 'bg-gray-300';
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${c}`} title={status} />;
}
function Tag({ children }: { children: React.ReactNode }) {
  return <span className="ml-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">{children}</span>;
}
function ago(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'soon';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
